import {
  IQuoteBasicRespSchema,
  QuoteStatusEnum,
  QuoteTypeEnum,
  QuoteTypeEnumDisplay,
} from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MText } from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  currencyBodyTemplate,
  dateTimeBodyTemplate,
  durationInMonthsTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
  userAvatarBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { AccountLookupPopover } from '../../../components/Monetize/LookupPopover';
import { ContractLookupPopover } from '../../../components/Monetize/LookupPopover/ContractLookupPopover';
import {
  QuoteStatusEnumDisplay,
  QuoteStatusTagStyle,
} from '../../../constants/quotes';
import { getQuoteBaseRoute } from '../../../constants/routes';

const columnHelper = createColumnHelper<IQuoteBasicRespSchema>();

export interface QuoteListColumnContext extends ColumnContextBase<IQuoteBasicRespSchema> {
  /** Controls whether the Account cell renders a lookup popover. */
  canCreateQuote: boolean;
  /** ACL: whether the Owner column is available. */
  canReadQuoteOwner: boolean;
}

/** tableId: FilterStateKeys.QUOTE_LIST */
export const QUOTE_LIST_CATALOG: ColumnCatalog<
  IQuoteBasicRespSchema,
  QuoteListColumnContext
> = [
  {
    id: 'description',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('description', {
        ...getColHeaderWithSize('Name', 280),
        ...getColHeaderFilters('TEXT'),
        enableSorting: true,
        cell: nameWithIdBodyTemplate<IQuoteBasicRespSchema, string>({
          nameProp: 'description',
          idProp: 'id',
          idLinkFn: (id) => getQuoteBaseRoute(id),
        }),
      }),
  },
  {
    id: 'accountName',
    label: 'Account',
    build: ({ canCreateQuote }) =>
      columnHelper.accessor('accountName', {
        ...getColHeaderWithSize('Account', 220),
        ...getColHeaderFilters('RECORD_SEARCH', {
          filterKey: 'accountId',
          recordSearch: {
            endpoint: '/api/accounts',
            itemTitle: 'accountName',
            itemValue: 'id',
            label: 'Filter by Account',
            sort: 'accountName',
          },
        }),
        enableSorting: true,
        cell: ({ row }) => {
          const data = row.original;
          if (canCreateQuote) {
            return (
              <AccountLookupPopover
                displayLabel={row.original.accountName}
                value={row.original.accountId}
              />
            );
          }
          return <MText lineClamp={1}>{data.accountName || '-'}</MText>;
        },
      }),
  },
  {
    id: 'ownerName',
    label: 'Owner',
    isAvailable: ({ canReadQuoteOwner }) => canReadQuoteOwner,
    build: () =>
      columnHelper.accessor('ownerName', {
        ...getColHeaderWithSize('Owner', 95),
        ...getColHeaderFilters('OWNER', { filterKey: 'owner' }),
        enableSorting: false,
        cell: userAvatarBodyTemplate(),
      }),
  },
  {
    id: 'type',
    label: 'Type',
    build: () =>
      columnHelper.accessor('type', {
        ...getColHeaderWithSize('Type', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            QuoteTypeEnum,
            QuoteTypeEnumDisplay,
          ),
        }),
        cell: enumDisplayBodyTemplate({ displayMap: QuoteTypeEnumDisplay }),
        enableSorting: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            QuoteStatusEnum,
            QuoteStatusEnumDisplay,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: QuoteStatusEnumDisplay,
          styleMap: QuoteStatusTagStyle,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'amount',
    label: 'Total',
    build: () =>
      columnHelper.accessor('amount', {
        ...getColHeaderWithSize('Total', 150),
        ...getColHeaderFilters('NUMBER'),
        cell: currencyBodyTemplate({ currencyProperty: 'currency' }),
        enableSorting: true,
      }),
  },
  {
    id: 'contractId',
    label: 'Contract',
    build: () =>
      columnHelper.accessor('contractId', {
        ...getColHeaderWithSize('Contract', 225),
        enableSorting: true,
        cell: ({ row }) =>
          row.original.contractId ? (
            <ContractLookupPopover
              displayLabel={`${toDateShort(
                row.original.contractStartDate,
              )} - ${toDateShort(row.original.contractEndDate)}`}
              value={row.original.contractId}
            />
          ) : null,
      }),
  },
  {
    id: 'contractStartDate',
    label: 'Start Date',
    build: () =>
      columnHelper.accessor('contractStartDate', {
        ...getColHeaderWithSize('Start Date', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'contractEndDate',
    label: 'End Date',
    build: () =>
      columnHelper.accessor('contractEndDate', {
        ...getColHeaderWithSize('End Date', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'contractLength',
    label: 'Length',
    build: () =>
      columnHelper.accessor('contractLength', {
        ...getColHeaderWithSize('Length', 115),
        cell: durationInMonthsTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IQuoteBasicRespSchema>(),
];
